import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, stripEmojis } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantSelectedEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import {
  updateLineItemPersonalizationEntry,
  SKIP_PERSONALIZATION_PROPERTY_KEY,
  withSkipPersonalizationAttribute,
} from '@theme/line-item-personalization-cart';
import { isBundleDetectionBlocked } from '@theme/bundle-detection-controls';
import '@theme/fly-to-cart';
import { createPersonalizationRecoverySnapshot } from '@theme/personalization-recovery';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const ADDON_TYPE_GIFT_WRAP = 'giftWrap';
const ADDON_TYPE_HOOD_MONOGRAM = 'hoodMonogram';
const GIFT_WRAP_ADDON_PROPERTY_KEY = '_gift_wrap_addon';
const HOOD_MONOGRAM_ADDON_PROPERTY_KEY = '_hood_monogram_addon';
const PARENT_PID_PROPERTY_KEY = '_parent_pid';
const PERSONALIZATION_PID_PROPERTY_KEY = 'properties[_pid]';
const PERSONALIZATION_SELECTED_LETTER_TYPE_KEY = '_selected-letter-type';
const PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY = 'properties[Gift Message]';
const INITIALS_BY_TAG_PRODUCT_INITIALS_MIN_LENGTH = 3;
const INITIALS_BY_TAG_PRODUCT_INITIALS_MAX_LENGTH = 3;
const PERSONALIZATION_OVERSIZED_PROPERTY_KEY = 'properties[_oversized]';
const NO_PERSONALIZATION_CHANGES_PROPERTY_KEY = 'properties[_no-personalization-changes]';
const SKIP_PERSONALIZATION_FORM_PROPERTY_KEY = `properties[${SKIP_PERSONALIZATION_PROPERTY_KEY}]`;
// Logo upload is a standalone add-to-cart property, independent of the
// personalization cart-attribute system, so it must survive every strip path.
const LOGO_UPLOAD_PROPERTY_KEY = 'properties[Upload your logo]';
// Shopify gift card recipient fields are native line item properties, not monogram data,
// so they must survive every personalization detection and strip path.
const SHOPIFY_GIFT_CARD_PROPERTY_KEYS = [
  'properties[Recipient email]',
  'properties[Recipient name]',
  'properties[Message]',
  'properties[Send on]',
  'properties[__shopify_send_gift_card_to_recipient]',
  'properties[__shopify_offset]',
];
const PERSONALIZATION_KNOWN_PROPERTY_KEYS = [
  'properties[Design]',
  'properties[Thread Color]',
  'properties[Vinyl Color]',
  'properties[Foil Color]',
  'properties[_thread-color]',
  'properties[_swatch-color]',
  'properties[_selected-id]',
  'properties[_selected-color]',
];

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Confirms that the customer intends to use the product's default personalization.
   * @param {(HTMLElement & { isUsingDefaultPersonalization?: () => boolean }) | null} personalizationFormEl
   * @returns {{ proceed: boolean, acceptedDefaultPersonalization: boolean }}
   */
  designConfirmation(personalizationFormEl) {
    /** @type {{ proceed: boolean, acceptedDefaultPersonalization: boolean }} */
    const result = { proceed: true, acceptedDefaultPersonalization: false };

    if (!personalizationFormEl) return result;
    if (personalizationFormEl.isUsingDefaultPersonalization?.() !== true) return result;

    const defaultTextInput = /** @type {HTMLInputElement | null} */ (
      personalizationFormEl.querySelector('input[name="defaultPersonalizationText"]')
    );
    const defaultDesignText = defaultTextInput?.value || '';
    const confirmationMessage = defaultDesignText
      ? `Are you sure you'd like to use the initials "${defaultDesignText}"?`
      : 'Are you sure you would like to use the default personalization?';
    if (window.confirm(confirmationMessage)) {
      result.acceptedDefaultPersonalization = true;
      return result;
    }

    return { proceed: false, acceptedDefaultPersonalization: false };
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    const formComponent = this.closest('product-form-component');
    if (formComponent?.bundleDetectionPending) {
      event.preventDefault();
      return;
    }
    if (formComponent?.bundlePersonalization) {
      if (!formComponent.bundlePersonalization.validate()) {
        event.preventDefault();
        return;
      }
      this.handleCartAction(event);
      return;
    }
    const personalizationForm = formComponent?.querySelector('[ref="productPersonalizationComponent"]');
    const personalizationChecked = formComponent?.querySelector('.personalization__checkbox:checked');
    const logoUploadInput = /** @type {HTMLInputElement | null} */ (
      formComponent?.querySelector('[data-logo-upload-input]') || null
    );
    const hasLogoUpload = logoUploadInput instanceof HTMLInputElement && Boolean(logoUploadInput.files?.length);
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (formComponent);
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    // Check for 'personalizationForm' and 'personalizationChecked' and run a window confirmation
    // If there is no 'personalizationForm or 'personalizationChecked', submit the form as expected
    if (personalizationForm && !personalizationChecked && !hasLogoUpload) {
      event.preventDefault();

      const confirmation = this.designConfirmation(personalizationForm);
      if (!confirmation.proceed) return;

      if (productForm && confirmation.acceptedDefaultPersonalization) {
        productForm.dataset.acceptedDefaultPersonalization = 'true';
      }

      // If window confirmation response is true, submit the form
      form.requestSubmit();
      this.handleCartAction(event);
      return;
    }

    this.handleCartAction(event);
  }

  /**
   * @param {MouseEvent & { target: HTMLElement; }} evt
   */
  handleCartAction(evt) {
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !evt.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 * @typedef {{id: number, quantity: number, properties?: Record<string, string>, addonType?: string}} NestedCartAddItem
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLElement | undefined} attnWaitlistTrigger - The Attentive back-in-stock trigger mount point.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  #submitting = false;
  #variantRevision = 0;
  get bundleDetectionPending() {
    const bundle = this.closest('.shopify-section')?.querySelector(
      `bundle-products[data-product-id="${this.dataset.productId}"]`
    );
    return isBundleDetectionBlocked(bundle?.dataset.state, Boolean(bundle?.personalization));
  }

  get bundlePersonalization() {
    return (
      this.closest('.shopify-section')?.querySelector(`bundle-products[data-product-id="${this.dataset.productId}"]`)
        ?.personalization || null
    );
  }

  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * @param {ParentNode} documentRoot
   * @param {HTMLElement} primaryForm
   */
  static #removeOrphanAttnWaitlistButtons(documentRoot, primaryForm) {
    const primaryTrigger = primaryForm.querySelector('#attn-waitlist-trigger, [ref="attnWaitlistTrigger"]');
    const selectors = '.attn-waitlist-trigger-btn, button[class*="attn-waitlist"], a[class*="attn-waitlist"]';

    documentRoot.querySelectorAll(selectors).forEach((element) => {
      if (primaryTrigger instanceof HTMLElement && primaryTrigger.contains(element)) return;
      element.remove();
    });
  }

  /**
   * Matches the visible sold-out state Attentive uses on Shopify PDPs.
   * @param {HTMLElement} primaryForm
   */
  static #isWaitlistEligible(primaryForm) {
    const addToCartButton = primaryForm.querySelector('[ref="addToCartButton"]');
    return addToCartButton instanceof HTMLButtonElement && addToCartButton.classList.contains('attn-oos');
  }

  /**
   * Nudge Attentive to re-scan the waitlist mount after AJAX product changes.
   * @param {HTMLElement} primaryForm
   */
  static #nudgeAttentiveWaitlist(primaryForm) {
    const variantInput = primaryForm.querySelector('[ref="variantId"], input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  static nudgeAttentiveWaitlist(scope = document) {
    const primaryForm = ProductFormComponent.#resolvePrimaryProductForm(scope);
    if (primaryForm) ProductFormComponent.#nudgeAttentiveWaitlist(primaryForm);
  }

  static isWaitlistEligible(scope = document) {
    const primaryForm = ProductFormComponent.#getPrimaryPdpProductForm(scope);
    return primaryForm ? ProductFormComponent.#isWaitlistEligible(primaryForm) : false;
  }

  /**
   * @param {ParentNode} [scope]
   * @returns {HTMLElement | null}
   */
  static #getPrimaryPdpProductForm(scope = document) {
    const documentRoot = scope instanceof Document ? scope : (scope.ownerDocument ?? document);
    return /** @type {HTMLElement | null} */ (
      documentRoot.querySelector('.product-information product-form-component')
    );
  }

  /**
   * @param {HTMLElement} form
   */
  static #isPrimaryPdpProductForm(form) {
    return form === ProductFormComponent.#getPrimaryPdpProductForm(form);
  }

  /**
   * @param {ParentNode} scope
   * @returns {HTMLElement | null}
   */
  static #resolvePrimaryProductForm(scope) {
    return ProductFormComponent.#getPrimaryPdpProductForm(scope);
  }

  /**
   * Keeps a single Attentive waitlist mount in sync after stitched product morphs and variant changes.
   * @param {ParentNode} [scope]
   * @param {{ remount?: boolean; orphansOnly?: boolean }} [options]
   */
  static syncAttnWaitlistTrigger(scope = document, { remount = false, orphansOnly = false } = {}) {
    const documentRoot = scope instanceof Document ? scope : (scope.ownerDocument ?? document);
    const primaryForm = ProductFormComponent.#getPrimaryPdpProductForm(documentRoot);
    if (!(primaryForm instanceof HTMLElement)) return;

    /** @type {HTMLElement | null} */
    let primaryTrigger = primaryForm.querySelector('[ref="attnWaitlistTrigger"], #attn-waitlist-trigger');

    documentRoot.querySelectorAll('#attn-waitlist-trigger').forEach((trigger) => {
      if (trigger !== primaryTrigger) trigger.remove();
    });

    documentRoot.querySelectorAll('#attn-waitlist-eligibility').forEach((script) => {
      if (!primaryForm.contains(script)) script.remove();
    });

    ProductFormComponent.#removeOrphanAttnWaitlistButtons(documentRoot, primaryForm);

    if (orphansOnly || !(primaryTrigger instanceof HTMLElement)) return;

    const isWaitlistEligible = ProductFormComponent.#isWaitlistEligible(primaryForm);

    if (!isWaitlistEligible) {
      primaryTrigger.replaceChildren();
      primaryTrigger.id = 'attn-waitlist-trigger';
      primaryTrigger.dataset.waitlistEligible = 'false';
      primaryTrigger.hidden = true;
      primaryTrigger.setAttribute('aria-hidden', 'true');
      ProductFormComponent.#removeOrphanAttnWaitlistButtons(documentRoot, primaryForm);
      return;
    }

    if (remount) {
      const primaryEligibility = primaryForm.querySelector('#attn-waitlist-eligibility');
      if (primaryEligibility instanceof HTMLElement) {
        const eligibilityReplacement = /** @type {HTMLElement} */ (primaryEligibility.cloneNode(true));
        primaryEligibility.replaceWith(eligibilityReplacement);
      }

      const triggerReplacement = document.createElement('div');
      triggerReplacement.id = 'attn-waitlist-trigger';
      triggerReplacement.setAttribute('ref', 'attnWaitlistTrigger');
      primaryTrigger.replaceWith(triggerReplacement);
      primaryTrigger = triggerReplacement;
    }

    primaryTrigger.id = 'attn-waitlist-trigger';
    primaryTrigger.dataset.waitlistEligible = 'true';
    primaryTrigger.hidden = false;
    primaryTrigger.removeAttribute('aria-hidden');
    ProductFormComponent.#nudgeAttentiveWaitlist(primaryForm);
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });
    if (this.dataset.personalizeOptionEnabled === 'true') {
      this.dataset.personalizeRevealed = 'false';
      const personalizeToggle = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[data-personalize-opt-out-toggle]')
      );
      if (personalizeToggle) {
        personalizeToggle.checked = false;
      }
    }
    this.#syncPersonalizationFlowVisibility();
    this.addEventListener('input', this.#handleInitialsByTagProductInput, {
      signal,
    });
    this.addEventListener('input', this.#handleGiftMessageInput, { signal });

    if (this.querySelector('[ref="attnWaitlistTrigger"], #attn-waitlist-trigger')) {
      requestAnimationFrame(() => {
        ProductFormComponent.syncAttnWaitlistTrigger(this);
        scheduleAttnWaitlistCleanup();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #syncPersonalizationFlowVisibility() {
    if (this.dataset.personalizeOptionEnabled !== 'true') return;
    const personalizationComponent =
      /** @type {(HTMLElement & { syncPersonalizationVisibility?: () => void }) | null} */ (
        this.querySelector('personalization-component')
      );
    personalizationComponent?.syncPersonalizationVisibility?.();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Collects section IDs used for cart section re-rendering.
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    return sectionIds;
  }

  /**
   * Adds section rendering params to a FormData payload.
   * @param {FormData} formData
   * @param {string[]} sectionIds
   */
  #appendSectionsToFormData(formData, sectionIds) {
    if (sectionIds.length === 0) return;
    formData.append('sections', sectionIds.join(','));
  }

  /**
   * Adds section rendering params to a JSON payload.
   * @param {Record<string, any>} payload
   * @param {string[]} sectionIds
   */
  #appendSectionsToPayload(payload, sectionIds) {
    if (sectionIds.length === 0) return;
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  /**
   * Re-runs personalization sanitization and submit-time validation before cart submission.
   * @returns {boolean}
   */
  #validatePersonalizationBeforeSubmit() {
    if (this.bundlePersonalization) return this.bundlePersonalization.validate();
    if (this.dataset.personalizeOptionEnabled !== 'true') return true;

    const form = this.querySelector('form');
    if (form instanceof HTMLFormElement && this.#hasLogoUpload(form)) return true;

    const personalizationComponent =
      /** @type {(HTMLElement & { validatePersonalizationInput?: (options?: {
       *   enforceMinimum?: boolean,
       *   reportValidity?: boolean
       * }) => boolean }) | null} */ (this.querySelector('personalization-component'));

    if (!personalizationComponent?.validatePersonalizationInput) return true;

    return personalizationComponent.validatePersonalizationInput({
      enforceMinimum: true,
      reportValidity: true,
    });
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #sanitizeInitialsByTagProduct(value) {
    return value
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, INITIALS_BY_TAG_PRODUCT_INITIALS_MAX_LENGTH);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #syncInitialsByTagProductInput(input) {
    const sanitizedValue = this.#sanitizeInitialsByTagProduct(input.value);

    if (sanitizedValue !== input.value) {
      input.value = sanitizedValue;
    }

    const previewCharacters = this.querySelectorAll('[data-initials-by-tag-product-preview-character]');
    const previewCharacterOrder = [0, 2, 1];
    previewCharacters[0]?.parentElement?.setAttribute('aria-label', sanitizedValue);
    previewCharacters.forEach((character, index) => {
      character.textContent = sanitizedValue[previewCharacterOrder[index]] || '';
    });

    if (sanitizedValue.length < INITIALS_BY_TAG_PRODUCT_INITIALS_MIN_LENGTH) {
      input.setCustomValidity(`Please enter at least ${INITIALS_BY_TAG_PRODUCT_INITIALS_MIN_LENGTH} characters.`);
      return;
    }

    input.setCustomValidity('');
  }

  /**
   * @param {Event} event
   */
  #handleInitialsByTagProductInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-initials-by-tag-product]')) return;

    this.#syncInitialsByTagProductInput(target);
  };

  /**
   * Strips emojis from the PDP gift message textarea as the shopper types or pastes.
   * @param {Event} event
   */
  #handleGiftMessageInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) return;
    if (target.name !== PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY) return;

    this.#sanitizeGiftMessageInput(target);
  };

  /**
   * Removes emojis from a gift message field and restores the caret when possible.
   * @param {HTMLTextAreaElement} textarea
   */
  #sanitizeGiftMessageInput(textarea) {
    const previousValue = textarea.value;
    const sanitizedValue = stripEmojis(previousValue);
    if (sanitizedValue === previousValue) return;

    const selectionStart = textarea.selectionStart ?? previousValue.length;
    const sanitizedBeforeCursor = stripEmojis(previousValue.slice(0, selectionStart));

    textarea.value = sanitizedValue;
    const nextCursor = Math.min(sanitizedBeforeCursor.length, sanitizedValue.length);
    textarea.setSelectionRange(nextCursor, nextCursor);
  }

  /**
   * @returns {boolean}
   */
  #validateInitialsByTagProductBeforeSubmit() {
    const initialsInput = this.querySelector('[data-initials-by-tag-product]');
    if (!(initialsInput instanceof HTMLInputElement)) return true;

    this.#syncInitialsByTagProductInput(initialsInput);

    if (!initialsInput.checkValidity()) {
      initialsInput.reportValidity();
      return false;
    }

    return true;
  }

  /**
   * @returns {boolean}
   */
  #isInitialsByTagProductForm() {
    return this.dataset.initialsByTagProductEnabled === 'true';
  }

  /**
   * Builds the future cart attribute personalization payload from line item properties.
   * @param {FormData} formData
   * @param {string} [rawLetterType] - Original (un-overridden) letter type to store as the label
   * @returns {{ attributes: Array<{ key: string, value: string | { label: string, value: string } }> }}
   */
  #buildCartAttributePersonalizationPayload(formData, rawLetterType = '') {
    /** @type {{ attributes: Array<{ key: string, value: string | { label: string, value: string } }> }} */
    const payload = { attributes: [] };
    const excludedPropertyKeys = new Set([
      PERSONALIZATION_PID_PROPERTY_KEY,
      PERSONALIZATION_OVERSIZED_PROPERTY_KEY,
      PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY,
      LOGO_UPLOAD_PROPERTY_KEY,
      ...SHOPIFY_GIFT_CARD_PROPERTY_KEYS,
      ...PERSONALIZATION_KNOWN_PROPERTY_KEYS,
    ]);

    PERSONALIZATION_KNOWN_PROPERTY_KEYS.forEach((propertyKey) => {
      const value = formData.get(propertyKey);
      if (typeof value !== 'string' || value.trim() === '') return;

      payload.attributes.push({
        key: propertyKey.slice('properties['.length, -1),
        value: value.trim(),
      });
    });

    for (const [key, rawValue] of formData.entries()) {
      if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
      if (excludedPropertyKeys.has(key)) continue;
      if (typeof rawValue !== 'string' || rawValue.trim() === '') continue;
      const propertyLabel = key.slice('properties['.length, -1);
      if (propertyLabel.startsWith('_')) continue;

      payload.attributes.push({
        key: PERSONALIZATION_SELECTED_LETTER_TYPE_KEY,
        value: {
          label: rawLetterType || propertyLabel,
          value: rawValue.trim(),
        },
      });

      break;
    }

    return payload;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #hasPersonalizationOptOut(form) {
    const optOutToggle = form.querySelector('input[data-personalize-opt-out-toggle]');
    return optOutToggle instanceof HTMLInputElement && optOutToggle.checked;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #hasLogoUpload(form) {
    const logoUploadInput = form.querySelector('[data-logo-upload-input]');
    return logoUploadInput instanceof HTMLInputElement && Boolean(logoUploadInput.files?.length);
  }

  /**
   * Removes all personalization-related line item properties from add-to-cart FormData.
   * @param {FormData} formData
   */
  #stripPersonalizationPropertiesFromFormData(formData) {
    formData.delete(PERSONALIZATION_PID_PROPERTY_KEY);
    PERSONALIZATION_KNOWN_PROPERTY_KEYS.forEach((propertyKey) => {
      formData.delete(propertyKey);
    });

    const protectedPropertyKeys = new Set([
      PERSONALIZATION_OVERSIZED_PROPERTY_KEY,
      PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY,
      LOGO_UPLOAD_PROPERTY_KEY,
      ...SHOPIFY_GIFT_CARD_PROPERTY_KEYS,
    ]);

    for (const key of [...formData.keys()]) {
      if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
      if (protectedPropertyKeys.has(key)) continue;

      const propertyLabel = key.slice('properties['.length, -1);
      if (propertyLabel.startsWith('_') || propertyLabel === 'Gift Message') {
        formData.delete(key);
        continue;
      }

      formData.delete(key);
    }
  }

  /**
   * Removes legacy personalization line item properties from add-to-cart FormData.
   * @param {FormData} formData
   */
  #removeLegacyPersonalizationProperties(formData) {
    PERSONALIZATION_KNOWN_PROPERTY_KEYS.forEach((propertyKey) => {
      formData.delete(propertyKey);
    });

    const protectedPropertyKeys = new Set([
      PERSONALIZATION_PID_PROPERTY_KEY,
      PERSONALIZATION_OVERSIZED_PROPERTY_KEY,
      PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY,
      LOGO_UPLOAD_PROPERTY_KEY,
      ...SHOPIFY_GIFT_CARD_PROPERTY_KEYS,
      ...PERSONALIZATION_KNOWN_PROPERTY_KEYS,
    ]);

    for (const [key, rawValue] of formData.entries()) {
      if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
      if (protectedPropertyKeys.has(key)) continue;
      if (typeof rawValue !== 'string' || rawValue.trim() === '') continue;
      const propertyLabel = key.slice('properties['.length, -1);
      if (propertyLabel.startsWith('_')) continue;
      formData.delete(key);
      break;
    }
  }

  /**
   * Sends an add-to-cart request and parses its JSON response.
   * @param {FormData | string} body
   * @returns {Promise<any>}
   */
  async #addToCart(body) {
    const fetchCfg = fetchConfig('javascript', { body });
    const headers = new Headers(fetchCfg.headers || {});
    headers.set('Accept', 'application/json');

    if (typeof body === 'string') {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers,
    });

    return response.json();
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  #getAddOnType(input) {
    const fromDataset = input.dataset.addonType;
    if (fromDataset) return fromDataset;

    const container = input.closest('.product-form-addon--gift-wrap, .product-form-addon--hood-monogram');
    if (container?.classList.contains('product-form-addon--gift-wrap')) return ADDON_TYPE_GIFT_WRAP;
    if (container?.classList.contains('product-form-addon--hood-monogram')) return ADDON_TYPE_HOOD_MONOGRAM;
    return '';
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  #isAddOnInputSelected(input) {
    if (input.disabled) return false;
    if (input.type === 'checkbox' || input.type === 'radio') return input.checked;
    return true;
  }

  /**
   * Returns which optional nested add-ons the customer selected on the form.
   * @param {HTMLFormElement} form
   * @returns {{ giftWrap: boolean, hoodMonogram: boolean }}
   */
  #getSelectedAddOnFlags(form) {
    const addOnInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      form.querySelectorAll('input[data-addon-variant-id]')
    );
    /** @type {{ giftWrap: boolean, hoodMonogram: boolean }} */
    const flags = { giftWrap: false, hoodMonogram: false };

    addOnInputs.forEach((input) => {
      if (!this.#isAddOnInputSelected(input)) return;

      const addonType = this.#getAddOnType(input);
      if (addonType === ADDON_TYPE_GIFT_WRAP) flags.giftWrap = true;
      if (addonType === ADDON_TYPE_HOOD_MONOGRAM) flags.hoodMonogram = true;
    });

    return flags;
  }

  /**
   * @param {any} response
   * @returns {any[]}
   */
  #getLineItemsFromCartAddResponse(response) {
    if (!response || typeof response !== 'object') return [];
    if (Array.isArray(response.items)) return response.items;
    if (typeof response.key === 'string') return [response];
    return [];
  }

  /**
   * @param {any} response
   * @param {string | null | undefined} pid
   * @returns {string}
   */
  #resolveParentLineKeyFromCartAddResponse(response, pid) {
    if (typeof response?.key === 'string') return response.key;

    const items = this.#getLineItemsFromCartAddResponse(response);
    if (pid) {
      const byPid = items.find((item) => item.properties?._pid === pid);
      if (typeof byPid?.key === 'string') return byPid.key;
    }

    return typeof items[0]?.key === 'string' ? items[0].key : '';
  }

  /**
   * Collects checked/active add-ons from inputs configured with data attributes.
   * Expected attributes:
   * - data-addon-variant-id="123456789"
   * - data-addon-type="giftWrap" | "hoodMonogram" (optional)
   * - data-addon-quantity="1" (optional)
   * - data-addon-property-foo="bar" (optional, repeatable)
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   * @returns {NestedCartAddItem[]}
   */
  #getSelectedNestedAddOns(form, formData) {
    const addOnInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      form.querySelectorAll('input[data-addon-variant-id]')
    );
    /** @type {NestedCartAddItem[]} */
    const selectedAddOns = [];

    addOnInputs.forEach((input) => {
      if (!this.#isAddOnInputSelected(input)) return;

      const parsedVariantId = Number.parseInt(input.dataset.addonVariantId || '', 10);
      if (!Number.isFinite(parsedVariantId) || parsedVariantId <= 0) return;

      const parsedQuantity = Number.parseInt(input.dataset.addonQuantity || '1', 10);
      const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
      const addonType = this.#getAddOnType(input);

      /** @type {Record<string, string>} */
      const properties = { [SKIP_PERSONALIZATION_PROPERTY_KEY]: 'false' };
      if (addonType === ADDON_TYPE_GIFT_WRAP) {
        properties[GIFT_WRAP_ADDON_PROPERTY_KEY] = 'true';
      } else if (addonType === ADDON_TYPE_HOOD_MONOGRAM) {
        properties[HOOD_MONOGRAM_ADDON_PROPERTY_KEY] = 'true';
      }

      input.getAttributeNames().forEach((attributeName) => {
        const prefix = 'data-addon-property-';
        if (!attributeName.startsWith(prefix)) return;
        const propertyKey = attributeName.slice(prefix.length);
        const propertyValue = input.getAttribute(attributeName);
        if (!propertyKey || !propertyValue) return;
        properties[propertyKey] = propertyValue;
      });

      selectedAddOns.push({
        id: parsedVariantId,
        quantity,
        ...(addonType ? { addonType } : {}),
        ...(Object.keys(properties).length > 0 ? { properties } : {}),
      });
    });

    return selectedAddOns;
  }

  /**
   * @param {NestedCartAddItem[]} addOns
   * @param {string} parentPid
   * @returns {NestedCartAddItem[]}
   */
  #applyParentPidToAddOns(addOns, parentPid) {
    return addOns.map((addOn) => ({
      ...addOn,
      properties: {
        ...(addOn.properties || {}),
        [PARENT_PID_PROPERTY_KEY]: parentPid,
      },
    }));
  }

  /**
   * Adds selected add-ons as nested line items under the parent line key.
   * @param {string} parentLineKey
   * @param {NestedCartAddItem[]} addOns
   * @param {string[]} sectionIds
   * @returns {Promise<any | null>}
   */
  async #addNestedLineItems(parentLineKey, addOns, sectionIds) {
    if (!parentLineKey || addOns.length === 0) return null;

    /** @type {Record<string, any>} */
    const payload = {
      items: addOns.map((addOn) => ({
        id: addOn.id,
        quantity: addOn.quantity,
        parent_line_key: parentLineKey,
        ...(addOn.properties ? { properties: addOn.properties } : {}),
      })),
    };
    this.#appendSectionsToPayload(payload, sectionIds);

    return this.#addToCart(JSON.stringify(payload));
  }

  /**
   * Prefers the active variant-picker selection, then falls back to hidden input.
   * @returns {string}
   */
  #getCurrentParentVariantId() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog, product-card') || document;
    const picker =
      productId && section instanceof Element
        ? section.querySelector(`variant-picker[data-product-id="${productId}"]`)
        : null;

    let variantIdFromPicker = '';
    if (picker) {
      const selectedInput = /** @type {HTMLInputElement | null} */ (
        picker.querySelector('input:checked[data-variant-id]')
      );
      const selectedSelect = /** @type {HTMLSelectElement | null} */ (picker.querySelector('select'));
      const selectedOption = selectedSelect?.selectedOptions?.[0] || null;
      variantIdFromPicker =
        selectedInput?.dataset.variantId || selectedOption?.dataset.variantId || selectedSelect?.value || '';
    }

    const variantIdFromInput = this.refs.variantId?.value || '';
    return variantIdFromPicker || variantIdFromInput;
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;
    this.#submitting = true;
    try {
      const started = await this.#submit(event);
      if (!started) this.#submitting = false;
    } catch (error) {
      this.#submitting = false;
      throw error;
    }
  }

  /** @param {Event} event */
  async #submit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();
    if (this.dataset.bundleSubmitting === 'true') return;
    if (this.bundleDetectionPending) return;
    if (this.bundlePersonalization && !this.bundlePersonalization.validate()) return;

    const acceptedDefaultPersonalization = this.dataset.acceptedDefaultPersonalization === 'true';
    delete this.dataset.acceptedDefaultPersonalization;

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    // Also protect programmatic submits and Enter-key submissions in logo mode.
    const logoInput = this.querySelector('[data-logo-upload-input]');
    const quantityInput = this.querySelector('[data-personalization-quantity] input[name="quantity"]');
    if (logoInput instanceof HTMLInputElement && !logoInput.reportValidity()) return;
    if (quantityInput instanceof HTMLInputElement && !quantityInput.reportValidity()) return;


    if (acceptedDefaultPersonalization) {
      this.dataset.personalizeRevealed = 'true';
      const personalizationComponent = /** @type {(HTMLElement & { _syncLineItemProperties?: () => void }) | null} */ (
        this.querySelector('personalization-component')
      );
      personalizationComponent?._syncLineItemProperties?.();
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    if (!this.#validatePersonalizationBeforeSubmit()) {
      return;
    }

    if (!this.#validateInitialsByTagProductBeforeSubmit()) {
      return;
    }

    const personalizationComponent = !this.bundlePersonalization && this.querySelector('personalization-component');
    if (personalizationComponent?.prepareForSubmission) {
      const variantRevision = this.#variantRevision;
      allAddToCartContainers.forEach((container) => container.disable());
      try {
        await personalizationComponent.prepareForSubmission();
        if (!this.isConnected || this.#variantRevision !== variantRevision) {
          throw new Error('Your product selection changed. Please review it and try again.');
        }
        if (!form.reportValidity()) return;
        if (this.refs.quantitySelector?.canAddToCart?.().canAdd === false) {
          throw new Error('Please review the quantity and try again.');
        }
      } catch (error) {
        const message = error.message || 'Could not update your personalization preview. Please try again.';
        this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, message, {}));
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) textNode.textContent = message;
          else addToCartTextError.appendChild(document.createTextNode(message));
        }
        this.#setLiveRegionText(message);
        return;
      } finally {
        // Variant events own availability once a shopper changes their selection.
        if (this.#variantRevision === variantRevision) {
          allAddToCartContainers.forEach((container) => container.enable());
        }
      }
    }

    const formData = new FormData(form);
    /** @type {{ attributes: Array<{ key: string, value: string | { label: string, value: string } }> } | null} */
    let personalizationPayload = null;
    /** @type {string | null} */
    let personalizationPid = null;

    const bundlePersonalization = this.bundlePersonalization;
    const hasPersonalizationOptOut = !bundlePersonalization && this.#hasPersonalizationOptOut(form);
    const hasLogoUpload = !bundlePersonalization && this.#hasLogoUpload(form);

    if (bundlePersonalization) {
      this.#stripPersonalizationPropertiesFromFormData(formData);
    } else if (window.SDG?.Data?.enableCartAttributePersonalization === true) {
      if (hasPersonalizationOptOut) {
        this.#stripPersonalizationPropertiesFromFormData(formData);
        personalizationPid = crypto.randomUUID();
        formData.set(PERSONALIZATION_PID_PROPERTY_KEY, personalizationPid);
        personalizationPayload = withSkipPersonalizationAttribute({ attributes: [] }, true);
      } else if (hasLogoUpload) {
        this.#stripPersonalizationPropertiesFromFormData(formData);
        // Shopify replaces the file with its uploaded URL on the saved line.
        // A stable parent ID lets recovery retain that native file property.
        personalizationPid = crypto.randomUUID();
        formData.set(PERSONALIZATION_PID_PROPERTY_KEY, personalizationPid);
        personalizationPayload = withSkipPersonalizationAttribute({ attributes: [] }, false);
      } else {
        const selectedDesignInput = form.querySelector('[data-design-input]:checked');
        const rawLetterType =
          selectedDesignInput instanceof HTMLInputElement
            ? selectedDesignInput.dataset.designLetterTypeRaw?.trim() || ''
            : this.#isInitialsByTagProductForm()
              ? 'Initials'
              : '';
        const builtPersonalizationPayload = this.#buildCartAttributePersonalizationPayload(formData, rawLetterType);
        const hasPersonalizationPayloadAttributes = builtPersonalizationPayload.attributes.length > 0;

        if (hasPersonalizationPayloadAttributes) {
          personalizationPid = crypto.randomUUID();
          formData.set(PERSONALIZATION_PID_PROPERTY_KEY, personalizationPid);
          this.#removeLegacyPersonalizationProperties(formData);
          personalizationPayload = withSkipPersonalizationAttribute(builtPersonalizationPayload, false);
        } else {
          this.#stripPersonalizationPropertiesFromFormData(formData);
        }
      }
    } else if (hasPersonalizationOptOut || hasLogoUpload) {
      this.#stripPersonalizationPropertiesFromFormData(formData);
    }
    // Always record the customer's personalization opt-out choice, including for
    // non-customizable products (no opt-out toggle present, so this defaults to false).
    formData.set(SKIP_PERSONALIZATION_FORM_PROPERTY_KEY, hasPersonalizationOptOut ? 'true' : 'false');
    if (this.dataset.isOversizedItem === 'true') {
      formData.set('properties[_oversized]', 'true');
    }
    const giftMessagePropertyKey = PERSONALIZATION_GIFT_MESSAGE_PROPERTY_KEY;
    const giftMessageToggle = form.querySelector('input[data-gift-message-toggle]');
    if (giftMessageToggle instanceof HTMLInputElement && !giftMessageToggle.checked) {
      formData.delete(giftMessagePropertyKey);
    }

    const giftMessagePropertyValue = formData.get(giftMessagePropertyKey);
    if (typeof giftMessagePropertyValue === 'string') {
      // Defense in depth: strip emojis again in case an input event was bypassed.
      const sanitizedGiftMessage = stripEmojis(giftMessagePropertyValue);
      if (sanitizedGiftMessage.trim() === '') {
        formData.delete(giftMessagePropertyKey);
      } else if (sanitizedGiftMessage !== giftMessagePropertyValue) {
        formData.set(giftMessagePropertyKey, sanitizedGiftMessage);
      }

      const giftMessageTextarea = form.querySelector(`textarea[name="${giftMessagePropertyKey}"]`);
      if (giftMessageTextarea instanceof HTMLTextAreaElement) {
        this.#sanitizeGiftMessageInput(giftMessageTextarea);
      }
    }

    // Ensure parent add always uses the current product variant id.
    // This prevents stale/duplicate `id` fields from accidentally submitting an add-on variant.
    const parentVariantId = this.#getCurrentParentVariantId();
    if (!parentVariantId) {
      const errorMessage = 'Please select an available variant before adding to cart.';
      this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', errorMessage, errorMessage, {}));

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          addToCartTextError.appendChild(document.createTextNode(errorMessage));
        }
      }

      this.#setLiveRegionText(errorMessage);

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      setTimeout(() => {
        for (const container of allAddToCartContainers) {
          container.enable();
        }
      }, ERROR_BUTTON_REENABLE_DELAY);

      return;
    }
    if (this.refs.variantId) this.refs.variantId.value = parentVariantId;
    formData.set('id', parentVariantId);

    const cartSectionIds = this.#getCartSectionIds();
    this.#appendSectionsToFormData(formData, cartSectionIds);
    const selectedNestedAddOns = this.#getSelectedNestedAddOns(form, formData);
    const selectedAddOnFlags = this.#getSelectedAddOnFlags(form);
    const parentVariantIdNumber = Number.parseInt(parentVariantId, 10);
    const validNestedAddOns = selectedNestedAddOns.filter((addOn) => addOn.id !== parentVariantIdNumber);

    if (selectedNestedAddOns.length > 0 && validNestedAddOns.length === 0) {
      console.warn('Skipping nested add-ons because they match parent variant id');
    }

    const hasCartAttributeAddOnSelections =
      selectedAddOnFlags.giftWrap ||
      selectedAddOnFlags.hoodMonogram ||
      validNestedAddOns.some(
        (addOn) => addOn.addonType === ADDON_TYPE_GIFT_WRAP || addOn.addonType === ADDON_TYPE_HOOD_MONOGRAM
      );

    if (
      window.SDG?.Data?.enableCartAttributePersonalization === true &&
      !bundlePersonalization &&
      !personalizationPid &&
      hasCartAttributeAddOnSelections
    ) {
      personalizationPid = crypto.randomUUID();
      formData.set(PERSONALIZATION_PID_PROPERTY_KEY, personalizationPid);
      personalizationPayload = withSkipPersonalizationAttribute({ attributes: [] }, false);
    }

    if (!bundlePersonalization && validNestedAddOns.length > 0 && !personalizationPid) {
      personalizationPid = crypto.randomUUID();
      formData.set(PERSONALIZATION_PID_PROPERTY_KEY, personalizationPid);
      if (window.SDG?.Data?.enableCartAttributePersonalization === true) {
        personalizationPayload = withSkipPersonalizationAttribute({ attributes: [] }, false);
      }
    }

    if (acceptedDefaultPersonalization && !hasPersonalizationOptOut && !hasLogoUpload) {
      formData.set(NO_PERSONALIZATION_CHANGES_PROPERTY_KEY, 'true');
    }

    const submittedItemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    if (bundlePersonalization) {
      this.dataset.bundleSubmitting = 'true';
      bundlePersonalization.sync();
    }

    const addProductWithAddOns = async () => {
      const response = await this.#addToCart(formData);
      if (response.status) return response;

      /** @type {any | null} */
      let nestedAddResponse = null;
      const parentLineKey = this.#resolveParentLineKeyFromCartAddResponse(response, personalizationPid);

      if (validNestedAddOns.length > 0) {
        if (!parentLineKey) {
          console.warn('Unable to attach nested add-ons: parent line key missing from add-to-cart response');
        } else if (!personalizationPid) {
          console.warn('Unable to attach nested add-ons: parent pid missing');
        } else {
          try {
            nestedAddResponse = await this.#addNestedLineItems(
              parentLineKey,
              this.#applyParentPidToAddOns(validNestedAddOns, personalizationPid),
              cartSectionIds
            );
            if (nestedAddResponse?.sections) {
              response.sections = nestedAddResponse.sections;
            }
          } catch (nestedAddError) {
            console.error('Failed to add nested add-ons:', nestedAddError);
          }
        }
      }
      return response;
    };

    const addProductLines = async () => {
      if (bundlePersonalization) {
        const useCartAttributes = window.SDG?.Data?.enableCartAttributePersonalization === true;
        const { item, entry } = bundlePersonalization.prepareCart({
          variantId: parentVariantId,
          quantity: submittedItemCount,
          useCartAttributes,
        });
        // Submit the native bundle variant. Its child records belong to this
        // parent's single PID; they are never independent cart additions.
        formData.set('id', item.id);
        formData.set('quantity', String(item.quantity));
        for (const [key, value] of Object.entries(item.properties)) {
          formData.set(`properties[${key}]`, value);
        }
        personalizationPid = item.properties._pid;
        personalizationPayload = useCartAttributes ? entry : null;
      }
      // Persist the canonical entry before committing its `_pid` line. If this
      // write fails, the cart remains unchanged instead of becoming invalid.
      if (personalizationPid && personalizationPayload) {
        return updateLineItemPersonalizationEntry({
          pid: personalizationPid,
          payload: personalizationPayload,
          afterSave: addProductWithAddOns,
        });
      }

      return addProductWithAddOns();
    };

    addProductLines()
      .then(async (response) => {
        if (response.status) {
          // Shopify may still commit the maximum allowed quantity for an add
          // that returns an error payload, so retain the prepared entry.
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (personalizationPid && personalizationPayload) {
            void createPersonalizationRecoverySnapshot({ expectedPid: personalizationPid }).catch((recoveryError) => {
              console.warn('Unable to prepare personalization recovery:', recoveryError);
            });
          }

          // An inventory error can still commit the available quantity. Notify
          // cart/recovery readers even when this form has no inline error UI.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: submittedItemCount,
              productId: this.dataset.productId,
            })
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (personalizationPid) {
            void createPersonalizationRecoverySnapshot().catch((recoveryError) => {
              // Recovery is supplemental; it must never turn a successful add into a failure.
              console.warn('Unable to prepare personalization recovery:', recoveryError);
            });
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: submittedItemCount,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        // Keep a prepared entry when the add result is ambiguous: Shopify may
        // have committed the line before a network or response parsing error.
        // An orphan is safe and pruned later; deleting a live line's entry is not.
        console.error(error);
        if (bundlePersonalization || personalizationPayload) {
          const message = bundlePersonalization
            ? error.message || 'Could not add the bundle. Please check your cart before trying again.'
            : 'Could not add this personalized item. Please check your cart and try again.';
          this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, message, {}));
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) textNode.textContent = message;
            else addToCartTextError.appendChild(document.createTextNode(message));
          }
          this.#setLiveRegionText(message);
        }
      })
      .finally(() => {
        this.#submitting = false;
        if (bundlePersonalization) {
          delete this.dataset.bundleSubmitting;
          bundlePersonalization.sync();
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
    return true;
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    const currentProductId = this.dataset.productId;
    const eventProductId = event.detail.data.productId;
    const nextProductId = event.detail.data.newProduct?.id;

    // Ignore unrelated updates, but allow product-switch updates for this form.
    // Some flows publish { productId: oldId, newProduct: { id: newId } }.
    if (eventProductId !== currentProductId && nextProductId !== currentProductId) {
      return;
    }

    this.#variantRevision++;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.dataset.productUrl = event.detail.data.newProduct.url;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    const isPrimaryPdpForm = ProductFormComponent.#isPrimaryPdpProductForm(this);
    const wasWaitlistEligible = isPrimaryPdpForm ? ProductFormComponent.#isWaitlistEligible(this) : false;
    const isProductSwitch = isPrimaryPdpForm && Boolean(event.detail.data.newProduct);

    // Update the button state
    const variantUnavailable = event.detail.resource == null || event.detail.resource.available == false;
    if (variantUnavailable) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const targetProductId = event.detail.data.newProduct?.id || event.detail.data.productId || this.dataset.productId;
    const productFormSelector = `product-form-component[data-product-id="${targetProductId}"]`;
    const newFormRoot = event.detail.data.html.querySelector(productFormSelector);
    const newAddToCartButton = event.detail.data.html.querySelector(`${productFormSelector} [ref="addToCartButton"]`);
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton, {
        ...MORPH_OPTIONS,
        childrenOnly: false,
      });
    }

    if (acceleratedCheckoutButtonContainer) {
      if (variantUnavailable) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
      attnWaitlistTrigger,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
        // A removed notice can remain in refs until another observed mutation; use the mounted node.
        [
          '.restock-notice',
          /** @type {HTMLElement | undefined} */ (this.querySelector('.restock-notice') ?? undefined),
          /** @type {HTMLElement | undefined} */ (attnWaitlistTrigger),
        ],
        [
          '#attn-waitlist-eligibility',
          /** @type {HTMLElement | undefined} */ (this.querySelector('#attn-waitlist-eligibility') ?? undefined),
          attnWaitlistTrigger,
        ],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, newFormRoot?.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = newFormRoot?.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    const personalizationWrapper = this.querySelector('[data-personalize-trigger-wrapper]');
    const newPersonalization = newFormRoot?.querySelector('personalization-component');
    if (personalizationWrapper instanceof HTMLElement) {
      if (!personalizationWrapper.querySelector('personalization-component') && newPersonalization) {
        personalizationWrapper.appendChild(newPersonalization.cloneNode(true));
      }
      personalizationWrapper.hidden = variantUnavailable || this.dataset.personalizeOptionEnabled !== 'true';
    }

    this.#syncPersonalizationFlowVisibility();

    // Mirror variant availability onto the personalization CTA so an
    // out-of-stock size cannot be personalized. Add-to-cart is already
    // disabled above; without this the personalize button stayed clickable on
    // products with only some sizes out of stock (partial OOS).
    this.#setPersonalizeTriggerDisabled(variantUnavailable);

    if (isPrimaryPdpForm) {
      const isWaitlistEligible = ProductFormComponent.#isWaitlistEligible(this);
      const shouldRemountWaitlist = isProductSwitch || wasWaitlistEligible !== isWaitlistEligible;
      ProductFormComponent.syncAttnWaitlistTrigger(this, {
        remount: shouldRemountWaitlist,
      });
      scheduleAttnWaitlistCleanup();
    }

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Enables or disables the personalization CTA(s) in this product form to
   * match the selected variant's availability, mirroring the add-to-cart
   * button. A disabled <button> emits no click, so the personalization flow
   * cannot be triggered for an out-of-stock variant.
   * @param {boolean} disabled
   */
  #setPersonalizeTriggerDisabled(disabled) {
    const triggers = this.querySelectorAll('[data-personalize-trigger]');
    for (const trigger of triggers) {
      if (trigger instanceof HTMLButtonElement) {
        trigger.disabled = disabled;
      }
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   * @param {VariantSelectedEvent} event
   */
  #onVariantSelected = (event) => {
    // Ignore variant selections from other product contexts in the same section.
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return;

    const sourcePicker = eventTarget.closest('variant-picker');
    const sourceProductId = sourcePicker?.getAttribute('data-product-id');
    if (sourceProductId && sourceProductId !== this.dataset.productId) {
      return;
    }

    this.#variantRevision++;
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/** @type {number[]} */
let attnWaitlistCleanupTimeouts = [];

function scheduleAttnWaitlistCleanup() {
  attnWaitlistCleanupTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  attnWaitlistCleanupTimeouts = [
    window.setTimeout(() => {
      if (ProductFormComponent.isWaitlistEligible(document)) {
        ProductFormComponent.syncAttnWaitlistTrigger(document, {
          orphansOnly: true,
        });
        ProductFormComponent.nudgeAttentiveWaitlist(document);
        return;
      }

      ProductFormComponent.syncAttnWaitlistTrigger(document);
    }, 800),
  ];
}

document.addEventListener('mly:main-content-updated', () => {
  requestAnimationFrame(() => {
    ProductFormComponent.syncAttnWaitlistTrigger(document, { remount: true });
    syncProductUnavailableBanner();
    scheduleAttnWaitlistCleanup();
  });
});

/**
 * Removes stale unavailable banners left outside the active PDP section after stitched morphs.
 */
function syncProductUnavailableBanner() {
  const activeSection = document.querySelector('.shopify-section:has(.product-information)');
  if (!activeSection) return;

  document.querySelectorAll('.product-unavailable-banner').forEach((banner) => {
    if (!activeSection.contains(banner)) banner.remove();
  });

  activeSection.querySelectorAll('.product-unavailable-banner').forEach((banner, index) => {
    if (index > 0) banner.remove();
  });
}
